import { FC, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import { useRtl } from "@ryde/redux";
import { apiDataFetchingAtom, ICoursesCalendarSlotDto } from "@ryde/shared";

import {
  CoursesCalendarContext,
  coursesCalendarDefaultContext,
  CoursesCalendarWrapper,
} from "./parts";
import {
  CALENDAR_OUTER_MIN_HEIGHT_PX,
  CALENDAR_OUTER_VIEWPORT_OFFSET_PX,
} from "./parts/courses-calendar.constants";
import { CoursesCalendarShellStyled } from "./parts/courses-calendar.styled";

interface ICoursesCalendar {
  slots: ICoursesCalendarSlotDto[];
  onCreateSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  onAfterSlotProduce?: () => void;
}

export const CoursesCalendar: FC<ICoursesCalendar> = ({
  slots,
  onCreateSlotRide,
  onAfterSlotProduce,
}): ReactElement => {
  const { direction } = useRtl();
  const loadingData = useRecoilValue(apiDataFetchingAtom);
  const [{ coursesCalendarHeight, coursesCalendarWidth }, setMetrics] =
    useState({
      coursesCalendarHeight:
        coursesCalendarDefaultContext.coursesCalendarHeight,
      coursesCalendarWidth: coursesCalendarDefaultContext.coursesCalendarWidth,
    });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const resizeHandler = (): void => {
      if (!ref.current) {
        return;
      }
      const { width, height } = ref.current.getBoundingClientRect();
      setMetrics({
        coursesCalendarHeight: height,
        coursesCalendarWidth: width,
      });
    };

    resizeHandler();
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [loadingData, slots.length]);

  const contextValue = useMemo(
    () => ({
      coursesCalendarHeight,
      coursesCalendarWidth,
    }),
    [coursesCalendarHeight, coursesCalendarWidth],
  );

  return (
    <CoursesCalendarContext.Provider value={contextValue}>
      <CoursesCalendarShellStyled
        ref={ref}
        data-e2e="courses-calendar"
        style={{
          width: "100%",
          height: `calc(100vh - ${String(CALENDAR_OUTER_VIEWPORT_OFFSET_PX)}px)`,
          minHeight: CALENDAR_OUTER_MIN_HEIGHT_PX,
        }}
      >
        {coursesCalendarHeight > 0 && coursesCalendarWidth > 0 && (
          <CoursesCalendarWrapper
            key={direction}
            slots={slots}
            onCreateSlotRide={onCreateSlotRide}
            onAfterSlotProduce={onAfterSlotProduce}
          />
        )}
      </CoursesCalendarShellStyled>
    </CoursesCalendarContext.Provider>
  );
};
